import { Signal, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { TRANSLATIONS } from './languages';

type Params = Record<string, unknown>;

/** German text for `key` from the dictionary, with `{{param}}` filled in. */
export function germanText(key: string, params?: Params): string {
  let node: unknown = TRANSLATIONS.de;
  for (const part of key.split('.')) {
    node = (node as Record<string, unknown> | undefined)?.[part];
  }
  if (typeof node !== 'string') return key;
  return node.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, name: string) =>
    String(params?.[name] ?? '')
  );
}

/**
 * Text lookup for services that build display strings in code. Uses the
 * current language; falls back to German when no translation is loaded yet
 * (or no TranslateService exists, as in unit tests of plain services).
 */
export function injectText(): (key: string, params?: Params) => string {
  const translate = inject(TranslateService, { optional: true });
  return (key, params) => {
    const text = translate?.instant(key, params);
    return typeof text === 'string' && text !== key
      ? text
      : germanText(key, params);
  };
}

/** Current language as a signal, so `computed()` texts follow a language switch. */
export function injectCurrentLanguage(): Signal<string> {
  const translate = inject(TranslateService, { optional: true });
  if (!translate) return signal('de');
  return toSignal(translate.onLangChange.pipe(map(e => e.lang)), {
    initialValue: translate.getCurrentLang() ?? 'de',
  });
}
